import { useCallback } from 'react';
import { type UseFormClearErrors, type UseFormSetError } from 'react-hook-form';
import { useRouter } from 'next/router';

import { getUTMParams } from '@/utils/analytics/utmHelper';

import {
  QuizQuoteApiError,
  quoteResultStorage,
  sendQuizQuoteNotification,
  toQuizQuoteApiRequest,
  toQuizQuoteDisplayData,
} from '../utils';
import { type QuizQuoteFormValues } from '../utils/quizQuote.schema';

export type QuoteSubmitStrategy = (
  values: QuizQuoteFormValues,
) => void | Promise<void>;

const API_FIELD_TO_FORM_FIELD = {
  schoolName: 'school_name',
  accountName: 'account_name',
  accountPhone: 'account_phone',
  accountEmail: 'account_email',
  plan: 'plan',
  planType: 'plan_type',
  userCount: 'user_count',
  term: 'term',
  startDate: 'start_date',
  annotation: 'annotation',
} as const;

const FORM_FIELDS = new Set<string>([
  'school_name',
  'account_name',
  'account_phone',
  'account_email',
  'plan',
  'plan_type',
  'user_count',
  'term',
  'start_date',
  'annotation',
  'consent',
]);

function toFormFieldName(field: string): keyof QuizQuoteFormValues | null {
  if (field in API_FIELD_TO_FORM_FIELD) {
    return API_FIELD_TO_FORM_FIELD[
      field as keyof typeof API_FIELD_TO_FORM_FIELD
    ];
  }

  if (FORM_FIELDS.has(field)) {
    return field as keyof QuizQuoteFormValues;
  }

  return null;
}

function debugQuoteSubmit(message: string, payload?: unknown) {
  if (process.env.NEXT_PUBLIC_STAGE === 'PRD') return;

  // eslint-disable-next-line no-console
  console.debug(`[pricing/quote] ${message}`, payload);
}

export function useQuoteSubmit(
  setError: UseFormSetError<QuizQuoteFormValues>,
  clearErrors: UseFormClearErrors<QuizQuoteFormValues>,
): QuoteSubmitStrategy {
  const router = useRouter();

  return useCallback<QuoteSubmitStrategy>(
    async values => {
      clearErrors();

      const utm = getUTMParams();
      const quote = toQuizQuoteDisplayData(values);
      const request = toQuizQuoteApiRequest(values, utm, quote);

      try {
        debugQuoteSubmit('submit request', request);
        await sendQuizQuoteNotification(request);
        debugQuoteSubmit('notification sent');

        const key = quoteResultStorage.saveAndVerify({
          quote,
          submittedPlanType: values.plan_type,
        });
        debugQuoteSubmit('saved result key', key);
        await router.push({
          pathname: '/pricing/quote/result',
          query: { key },
        });
      } catch (error) {
        debugQuoteSubmit('submit failed', error);
        if (error instanceof QuizQuoteApiError) {
          const fields = error.body.error?.fields ?? {};
          let hasFieldError = false;

          Object.entries(fields).forEach(([field, message]) => {
            const formField = toFormFieldName(field);
            if (!formField || !message) return;

            hasFieldError = true;
            setError(formField, { type: 'server', message });
          });

          if (hasFieldError) return;

          setError('root.server', {
            type: 'server',
            message: error.message,
          });
          return;
        }

        setError('root.server', {
          type: 'server',
          message: '견적서 발행 중 문제가 발생했습니다. 다시 시도해 주세요.',
        });
      }
    },
    [clearErrors, router, setError],
  );
}
